import { computed, Directive, HostListener, input, output, signal } from '@angular/core';

@Directive({
    selector: '[fiDragScroll]',
    standalone: false,
    host: {
        '[class.drag-scroll-enabled]': 'fiDragScrollEnabled()',
        '[class.dragging]': 'isDragging()',
        '[style.cursor]': 'cursor()',
        '[style.touch-action]': 'isDragScrollActive() ? "pan-y" : null',
        '[style.user-select]': 'isDragScrollActive() ? "none" : null',
    },
})
export class FiDragScrollDirective {
    readonly fiDragScrollEnabled = input(false);
    readonly fiDragScrollIgnoreSelector = input<string>();

    readonly fiDragScrollStart = output<void>();
    readonly fiDragScroll = output<number>();
    readonly fiDragScrollEnd = output<void>();

    protected readonly isDragging = signal(false);
    protected readonly isDragScrollActive = computed(() => this.fiDragScrollEnabled() || this.isDragging());
    protected readonly cursor = computed((): string | null => {
        if (this.isDragging()) {
            return 'grabbing';
        }

        return this.fiDragScrollEnabled() ? 'grab' : null;
    });

    private dragStartX = 0;

    @HostListener('pointerdown', ['$event'])
    onDragStart(event: PointerEvent): void {
        if (!this.fiDragScrollEnabled() || event.button !== 0 || this.isIgnoredTarget(event.target)) {
            return;
        }

        this.dragStartX = event.clientX;
        this.isDragging.set(true);
        this.fiDragScrollStart.emit();
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        event.preventDefault();
    }

    @HostListener('pointermove', ['$event'])
    onDragMove(event: PointerEvent): void {
        if (!this.isDragging()) {
            return;
        }

        this.fiDragScroll.emit(event.clientX - this.dragStartX);
        event.preventDefault();
    }

    @HostListener('pointerup', ['$event'])
    @HostListener('pointercancel', ['$event'])
    @HostListener('lostpointercapture', ['$event'])
    onDragEnd(event: PointerEvent): void {
        if (!this.isDragging()) {
            return;
        }

        const target = event.currentTarget as HTMLElement;
        if (target.hasPointerCapture(event.pointerId)) {
            target.releasePointerCapture(event.pointerId);
        }

        this.isDragging.set(false);
        this.fiDragScrollEnd.emit();
    }

    private isIgnoredTarget(target: EventTarget | null): boolean {
        const ignoreSelector = this.fiDragScrollIgnoreSelector();

        return !!ignoreSelector && target instanceof HTMLElement && !!target.closest(ignoreSelector);
    }
}
